<template>
  <RevDrawer
    :close-button-label="i18n(translations.close)"
    :has-back-button="!!currentFormType"
    :name="MODAL_NAME"
    :title="modalTitle"
    @back="cleanHash"
    @close="cleanHash"
  >
    <template #body>
      <RevList v-if="!currentFormType" :has-external-borders="false">
        <RevListItemInteractive @click="handleNavigation(FORM_TYPES.UPDATE)">
          <template #label>
            {{ i18n(translations.updateExistingListingsPricesAndStock) }}
          </template>
          <template #suffix>
            <IconChevronRight />
          </template>
        </RevListItemInteractive>
        <RevListItemInteractive @click="handleNavigation(FORM_TYPES.IMPORT)">
          <template #label>
            {{ i18n(translations.importListingsThroughCsv) }}
          </template>
          <template #suffix>
            <IconChevronRight />
          </template>
        </RevListItemInteractive>
        <RevListItemInteractive
          @click="handleNavigation(FORM_TYPES.IMPORT_NEW)"
        >
          <template #label>
            {{ i18n(translations.importNewProductListingsThroughCsv) }}
          </template>
          <template #suffix>
            <IconChevronRight />
          </template>
        </RevListItemInteractive>
        <RevListItemInteractive @click="handleNavigation(FORM_TYPES.EXPORT)">
          <template #label>
            {{ i18n(translations.exportListingsInCsv) }}
          </template>
          <template #suffix>
            <IconChevronRight />
          </template>
        </RevListItemInteractive>
      </RevList>

      <Form
        v-else
        :publication-state
        :type="currentFormType"
        @result="handleResult"
        @submitted="handleSubmitted"
      />
    </template>
  </RevDrawer>
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from '#imports'
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevDrawer } from '@ds/components/Drawer'
import { RevList } from '@ds/components/List'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

import { PUBLICATION_STATE } from '~/scopes/listings/Listings.constants'
import { cleanHashWithoutTriggeringRouterRedirection } from '~/utils/cleanHashWithoutTriggeringRouterRedirection'

import Form from './Form/Form.vue'
import {
  FORM_TYPES,
  HASH_NAME,
  MODAL_NAME,
} from './ImportExportDrawer.constants'
import translations from './ImportExportDrawer.translations'

const i18n = useI18n()
const route = useRoute()
const router = useRouter()
const tracking = useTracking()
const { openSuccessToast, openErrorToast } = useTheToast()
const publicationState = computed(() => {
  return (route.meta?.publicationState as string) || PUBLICATION_STATE.ONLINE
})
const currentFormType = ref('')

const MODAL_TITLES = {
  [FORM_TYPES.EXPORT]: i18n(translations.exportListings),
  [FORM_TYPES.IMPORT]: i18n(translations.importListings),
  [FORM_TYPES.IMPORT_NEW]: i18n(translations.importNewProductListings),
  [FORM_TYPES.UPDATE]: i18n(translations.updateListings),
}

const modalTitle = computed(() => {
  return !currentFormType.value
    ? i18n(translations.modalTitle)
    : MODAL_TITLES[currentFormType.value]
})

function handleNavigation(formType: string) {
  tracking.trackClick({
    page_type: 'listings',
    zone: 'listings_action_panel',
    name: formType,
  })

  void router.push({
    ...route,
    query: {
      ...route.query,
    },
    hash: `#${HASH_NAME}=${formType}`,
  })
}

function cleanHash() {
  currentFormType.value = ''

  cleanHashWithoutTriggeringRouterRedirection()
}

function handleSubmitted() {
  closeModal(MODAL_NAME)
}

function handleResult(result: string) {
  switch (result) {
    case 'export/success':
      openSuccessToast({
        title: i18n(translations.toastExportSuccessTitle),
        content: i18n(translations.toastExportSuccessText),
      })
      break

    case 'export/error':
      openErrorToast({
        title: i18n(translations.toastActionsErrorTitle),
        content: i18n(translations.toastExportErrorText),
      })
      break

    case 'import/success':
      openSuccessToast({
        title: i18n(translations.toastImportSuccessTitle),
        content: i18n(translations.toastImportSuccessText),
      })
      break

    case 'import/error':
      openErrorToast({
        title: i18n(translations.toastActionsErrorTitle),
        content: i18n(translations.toastImportErrorText),
      })
      break

    case 'update/success':
      openSuccessToast({
        title: i18n(translations.toastUpdateSuccessTitle),
        content: i18n(translations.toastUpdateSuccessText),
      })
      break

    case 'update/error':
      openErrorToast({
        title: i18n(translations.toastActionsErrorTitle),
        content: i18n(translations.toastUpdateErrorText),
      })
      break

    default:
      break
  }
}

watch(
  () => route.hash,
  () => {
    if (route.hash.includes(HASH_NAME)) {
      const formType = route.hash.split('=')[1]

      if (Object.values(FORM_TYPES).includes(formType)) {
        currentFormType.value = formType
        openModal(MODAL_NAME)
      }
    } else {
      currentFormType.value = ''
    }
  },
  {
    immediate: true,
  },
)
</script>
